import { ELDER_COUNCIL } from '../config';
import { PrestigeManager } from '../managers/PrestigeManager';
import { GameScene } from '../scenes/GameScene';
import { achievementManager } from '../managers/AchievementManager';
import { gameStateManager } from '../managers/GameStateManager';

export interface PrestigeNodeDef {
    id: string;
    branchId: keyof typeof ELDER_COUNCIL;
    tierIndex: number;
    name: string;
    description: string;
    cost: number;
    x: number;
    y: number;
    requires: string[];
}

export class PrestigeTreeUI {
    private overlay: HTMLElement;
    private viewport: HTMLElement;
    private svgLayer: SVGElement;
    private nodesContainer: HTMLElement;
    private legacyValueDisplay: HTMLElement;
    private closeBtn: HTMLElement;

    private tooltip: HTMLElement;
    private ttTitle: HTMLElement;
    private ttDesc: HTMLElement;
    private ttCost: HTMLElement;
    private ttStatus: HTMLElement;

    private prestigeManager: PrestigeManager;
    private scene: GameScene | null = null;

    // Panning & Zooming state
    private isDragging = false;
    private startDragX = 0;
    private startDragY = 0;
    private currentX = 0;
    private currentY = 0;
    private currentZoom = 1;

    // Particle canvas
    private particleCanvas: HTMLCanvasElement | null = null;
    private particleCtx: CanvasRenderingContext2D | null = null;
    private particleAnimId: number = 0;
    private stars: Array<{ x: number; y: number; r: number; op: number; speed: number; phase: number }> = [];

    private nodesConfig: Map<string, PrestigeNodeDef> = new Map();

    constructor(prestigeManager: PrestigeManager) {
        this.prestigeManager = prestigeManager;

        this.overlay = document.getElementById('prestige-tree-overlay') as HTMLElement;
        this.viewport = document.getElementById('ptree-viewport') as HTMLElement;
        this.svgLayer = document.getElementById('ptree-svg') as unknown as SVGElement;
        this.nodesContainer = document.getElementById('ptree-nodes') as HTMLElement;
        this.legacyValueDisplay = document.getElementById('ptree-legacy-points') as HTMLElement;
        this.closeBtn = document.getElementById('btn-ptree-close') as HTMLElement;

        this.tooltip = document.getElementById('ptree-tooltip') as HTMLElement;
        this.ttTitle = document.getElementById('ptree-tt-title') as HTMLElement;
        this.ttDesc = document.getElementById('ptree-tt-desc') as HTMLElement;
        this.ttCost = document.getElementById('ptree-tt-cost') as HTMLElement;
        this.ttStatus = document.getElementById('ptree-tt-status') as HTMLElement;

        this.initNodesConfig();
        this.initParticles();
        this.setupEventListeners();
    }

    public attachScene(scene: GameScene) {
        this.scene = scene;
    }

    // ---- Node layout config ----

    private initNodesConfig() {
        const centerX = 150;
        const centerY = window.innerHeight / 2 || 400;
        const spacingX = 220;

        const branches = Object.keys(ELDER_COUNCIL) as (keyof typeof ELDER_COUNCIL)[];

        const yOffsetByBranch: Record<string, number> = {
            newbeginning: 0,
            warrior: -170,
            mason: 170,
            merchant: 340,
        };

        branches.forEach((branchId, bIndex) => {
            const branch = ELDER_COUNCIL[branchId];
            const yOff = yOffsetByBranch[branchId as string] ?? (bIndex * 170 - 170);
            const startY = centerY + yOff;

            branch.tiers.forEach((tier, tIndex) => {
                const id = `${branchId}_${tIndex}`;

                let reqs: string[] = [];
                if (tIndex > 0) {
                    reqs = [`${branchId}_${tIndex - 1}`];
                } else if (branchId !== 'newbeginning') {
                    reqs = ['newbeginning_0'];
                }

                let xPos = centerX + (tIndex * spacingX);
                if (branchId !== 'newbeginning') {
                    xPos += spacingX;
                }

                this.nodesConfig.set(id, {
                    id,
                    branchId,
                    tierIndex: tIndex,
                    name: tier.name,
                    description: tier.desc,
                    cost: tier.cost,
                    x: xPos,
                    y: startY,
                    requires: reqs
                });
            });
        });
    }

    // ---- Particle starfield ----

    private initParticles(): void {
        this.particleCanvas = document.getElementById('ptree-particle-canvas') as HTMLCanvasElement;
        if (!this.particleCanvas) return;
        this.particleCtx = this.particleCanvas.getContext('2d');
        this.resizeParticleCanvas();
        window.addEventListener('resize', () => this.resizeParticleCanvas());
    }

    private resizeParticleCanvas(): void {
        if (!this.particleCanvas) return;
        this.particleCanvas.width = window.innerWidth;
        this.particleCanvas.height = window.innerHeight;
        this.generateStars();
    }

    private generateStars(): void {
        if (!this.particleCanvas) return;
        const area = this.particleCanvas.width * this.particleCanvas.height;
        const count = Math.min(200, Math.floor(area / 4800));
        this.stars = Array.from({ length: count }, () => ({
            x: Math.random() * (this.particleCanvas?.width ?? 1024),
            y: Math.random() * (this.particleCanvas?.height ?? 768),
            r: Math.random() * 1.3 + 0.2,
            op: Math.random() * 0.55 + 0.12,
            speed: Math.random() * 0.0012 + 0.0004,
            phase: Math.random() * Math.PI * 2,
        }));
    }

    private startParticles(): void {
        if (!this.particleCtx || !this.particleCanvas) return;
        const ctx = this.particleCtx;
        const canvas = this.particleCanvas;
        let t = 0;

        const loop = () => {
            this.particleAnimId = requestAnimationFrame(loop);
            t += 0.016;

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Soft nebula backdrop
            const grad = ctx.createRadialGradient(
                canvas.width * 0.5, canvas.height * 0.4, 0,
                canvas.width * 0.5, canvas.height * 0.5, canvas.width * 0.65
            );
            grad.addColorStop(0, 'rgba(50, 10, 100, 0.10)');
            grad.addColorStop(0.5, 'rgba(20, 5, 60, 0.06)');
            grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Twinkling stars
            for (const star of this.stars) {
                const opacity = star.op * (0.45 + 0.55 * Math.sin(t * star.speed * 1000 + star.phase));
                ctx.beginPath();
                ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(220, 200, 145, ${opacity})`;
                ctx.fill();
            }
        };
        loop();
    }

    private stopParticles(): void {
        if (this.particleAnimId) {
            cancelAnimationFrame(this.particleAnimId);
            this.particleAnimId = 0;
        }
    }

    // ---- Event listeners ----

    private setupEventListeners() {
        this.closeBtn.addEventListener('click', () => this.hide());

        // Desktop: mouse drag panning
        this.viewport.addEventListener('mousedown', (e) => {
            this.isDragging = true;
            this.startDragX = e.clientX - this.currentX;
            this.startDragY = e.clientY - this.currentY;
            this.viewport.style.cursor = 'grabbing';
        });

        window.addEventListener('mouseup', () => {
            if (this.isDragging) {
                this.isDragging = false;
                this.viewport.style.cursor = 'grab';
            }
        });

        window.addEventListener('mousemove', (e) => {
            if (!this.isDragging) return;
            e.preventDefault();
            this.currentX = e.clientX - this.startDragX;
            this.currentY = e.clientY - this.startDragY;
            this.updateTransform();
        });

        // Desktop: scroll wheel zoom — zoom toward mouse cursor position
        this.viewport.addEventListener('wheel', (e) => {
            e.preventDefault();
            const rect = this.viewport.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            const oldZoom = this.currentZoom;
            const zoomDelta = e.deltaY * -0.0009;
            const newZoom = Math.max(0.25, Math.min(oldZoom + zoomDelta, 2.5));
            const scaleFactor = newZoom / oldZoom;
            // Shift pan so the world-point under the cursor stays fixed
            this.currentX = mouseX - scaleFactor * (mouseX - this.currentX);
            this.currentY = mouseY - scaleFactor * (mouseY - this.currentY);
            this.currentZoom = newZoom;
            this.updateTransform();
        }, { passive: false });

        // Mobile: touch panning
        let touchStartX = 0;
        let touchStartY = 0;
        let lastPinchDist = 0;
        let lastPinchMidX = 0;
        let lastPinchMidY = 0;

        this.viewport.addEventListener('touchstart', (e) => {
            if (e.touches.length === 1) {
                this.isDragging = true;
                touchStartX = e.touches[0].clientX - this.currentX;
                touchStartY = e.touches[0].clientY - this.currentY;
                this.startDragX = touchStartX;
                this.startDragY = touchStartY;
            } else if (e.touches.length === 2) {
                this.isDragging = false;
                lastPinchDist = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY
                );
                const rect = this.viewport.getBoundingClientRect();
                lastPinchMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
                lastPinchMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
            }
        }, { passive: true });

        this.viewport.addEventListener('touchmove', (e) => {
            if (e.touches.length === 1 && this.isDragging) {
                this.currentX = e.touches[0].clientX - this.startDragX;
                this.currentY = e.touches[0].clientY - this.startDragY;
                this.updateTransform();
            } else if (e.touches.length === 2) {
                const dist = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY
                );
                if (lastPinchDist > 0) {
                    const rect = this.viewport.getBoundingClientRect();
                    const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
                    const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
                    const oldZoom = this.currentZoom;
                    const newZoom = Math.max(0.25, Math.min(oldZoom * (dist / lastPinchDist), 2.5));
                    const scaleFactor = newZoom / oldZoom;
                    // Zoom toward pinch midpoint
                    this.currentX = midX - scaleFactor * (lastPinchMidX - this.currentX);
                    this.currentY = midY - scaleFactor * (lastPinchMidY - this.currentY);
                    this.currentZoom = newZoom;
                    this.updateTransform();
                    lastPinchMidX = midX;
                    lastPinchMidY = midY;
                }
                lastPinchDist = dist;
            }
        }, { passive: true });

        this.viewport.addEventListener('touchend', () => {
            this.isDragging = false;
            lastPinchDist = 0;
        }, { passive: true });
    }

    private updateTransform() {
        const t = `translate(${this.currentX}px, ${this.currentY}px) scale(${this.currentZoom})`;
        this.nodesContainer.style.transform = t;
        this.svgLayer.style.transform = t;
    }

    // ---- Show / Hide ----

    public show() {
        if (this.scene) {
            this.scene.scene.pause();
        }

        const rootNode = this.nodesConfig.get('newbeginning_0');
        const viewW = window.innerWidth;
        const viewH = window.innerHeight;

        // Smaller default zoom on mobile
        this.currentZoom = viewW < 600 ? 0.42 : 0.78;

        if (rootNode) {
            this.currentX = (viewW / 2) - (rootNode.x * this.currentZoom) - 180;
            this.currentY = (viewH / 2) - (rootNode.y * this.currentZoom);
        } else {
            this.currentX = 0;
            this.currentY = 0;
        }

        this.updateTransform();
        this.updateUI();
        this.overlay.classList.remove('hidden');
        this.startParticles();
    }

    public hide() {
        this.overlay.classList.add('hidden');
        this.stopParticles();
        if (this.scene) {
            this.scene.onPrestigeTreeClosed();
            this.scene.scene.resume();
        }
    }

    private updateUI() {
        this.legacyValueDisplay.textContent = this.prestigeManager.getAvailableLegacy().toString();
        this.renderTree();
    }

    // ---- Node state helpers ----

    private isNodeUnlocked(id: string): boolean {
        const node = this.nodesConfig.get(id);
        if (!node) return false;
        const currentTier = this.prestigeManager.getElderTier(node.branchId as any);
        return currentTier > node.tierIndex;
    }

    private canBuyNode(id: string): boolean {
        const node = this.nodesConfig.get(id);
        if (!node) return false;
        if (this.isNodeUnlocked(id)) return false;
        if (this.prestigeManager.getAvailableLegacy() < node.cost) return false;
        if (node.requires.length > 0) {
            if (!this.isNodeUnlocked(node.requires[0])) return false;
        }
        const currentTier = this.prestigeManager.getElderTier(node.branchId as any);
        return node.tierIndex === currentTier;
    }

    // ---- Tree rendering ----

    private renderTree() {
        this.nodesContainer.innerHTML = '';
        this.svgLayer.innerHTML = '';

        const branchIcons: Record<string, string> = {
            newbeginning: '⭐',
            warrior: '⚔️',
            mason: '🧱',
            merchant: '⚖️',
        };

        // Draw branch lane labels (behind nodes, from SVG left edge)
        const renderedBranches = new Set<string>();

        this.nodesConfig.forEach((node) => {
            // ---- SVG connection lines ----
            if (node.requires.length > 0) {
                const reqNode = this.nodesConfig.get(node.requires[0]);
                if (reqNode) {
                    const isUnlocked = this.isNodeUnlocked(node.id);
                    const canBuy = this.canBuyNode(node.id);
                    const pathD = `M ${reqNode.x} ${reqNode.y} L ${node.x} ${node.y}`;

                    if (isUnlocked) {
                        // Layer 1 — fat purple glow (breathing)
                        const glow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                        glow.setAttribute('d', pathD);
                        glow.setAttribute('pathLength', '100');
                        glow.setAttribute('class', 'ptree-line-glow unlocked');
                        this.svgLayer.appendChild(glow);

                        // Layer 2 — crisp bright core
                        const core = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                        core.setAttribute('d', pathD);
                        core.setAttribute('pathLength', '100');
                        core.setAttribute('class', 'ptree-line unlocked');
                        this.svgLayer.appendChild(core);

                        // Layer 3 — traveling pulse orb (staggered per line)
                        const pulse = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                        pulse.setAttribute('d', pathD);
                        pulse.setAttribute('pathLength', '100');
                        pulse.setAttribute('class', 'ptree-line-pulse unlocked');
                        const branchOrder: Record<string, number> = { newbeginning: 0, warrior: 1, mason: 2, merchant: 3 };
                        const bOrd = branchOrder[node.branchId as string] ?? 0;
                        const delay = ((bOrd * 1.1 + node.tierIndex * 0.65) % 3.5).toFixed(2);
                        pulse.style.animationDelay = `${delay}s`;
                        this.svgLayer.appendChild(pulse);
                    } else {
                        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                        path.setAttribute('d', pathD);
                        path.setAttribute('pathLength', '100');
                        path.setAttribute('class', `ptree-line ${canBuy ? 'available' : 'locked'}`);
                        this.svgLayer.appendChild(path);
                    }
                }
            }

            // ---- Branch lane label (first node in branch) ----
            if (!renderedBranches.has(node.branchId as string) && node.tierIndex === 0) {
                renderedBranches.add(node.branchId as string);
                const branchColor = ELDER_COUNCIL[node.branchId].color;
                const branchLabel = ELDER_COUNCIL[node.branchId].label;

                const lbl = document.createElement('div');
                lbl.className = 'ptree-branch-label';
                lbl.style.top = `${node.y}px`;
                lbl.innerHTML = `
                    <div class="ptree-branch-dot" style="background: ${branchColor}"></div>
                    <span class="ptree-branch-name">${branchLabel}</span>
                `;
                this.nodesContainer.appendChild(lbl);
            }

            // ---- Hex node ----
            const isUnlocked = this.isNodeUnlocked(node.id);
            const canBuy = this.canBuyNode(node.id);
            const branchColor = ELDER_COUNCIL[node.branchId].color;

            // Wrapper: positions both the hex and the label below it
            const wrapper = document.createElement('div');
            wrapper.className = 'ptree-node-wrapper';
            wrapper.style.left = `${node.x}px`;
            wrapper.style.top = `${node.y}px`;

            const isOrigin = node.id === 'newbeginning_0';
            const el = document.createElement('div');
            el.className = `ptree-node${isOrigin ? ' origin' : ''} ${isUnlocked ? 'unlocked' : canBuy ? 'available' : 'locked'}`;

            // Glow via CSS filter (works with clip-path)
            if (isUnlocked) {
                el.style.filter = `drop-shadow(0 0 10px ${branchColor}bb)`;
            } else if (canBuy) {
                el.style.filter = `drop-shadow(0 0 6px ${branchColor}77)`;
            }

            const tierNum = node.tierIndex + 1;
            const totalTiers = ELDER_COUNCIL[node.branchId].tiers.length;
            el.innerHTML = `
                <div class="ptree-icon">${branchIcons[node.branchId as string] || '✨'}</div>
                <div class="ptree-node-tier">${tierNum}/${totalTiers}</div>
            `;

            // Name label below the hex (outside clip-path)
            const nameLabel = document.createElement('div');
            nameLabel.className = `ptree-node-name ${isUnlocked ? 'unlocked' : canBuy ? 'available' : 'locked'}`;
            nameLabel.textContent = node.name;

            wrapper.appendChild(el);
            wrapper.appendChild(nameLabel);

            // Tooltip: desktop hover
            wrapper.addEventListener('mouseenter', (e) => this.showTooltip(node, e));
            wrapper.addEventListener('mousemove', (e) => this.moveTooltip(e));
            wrapper.addEventListener('mouseleave', () => this.hideTooltip());

            // Tooltip: mobile touch (show at bottom)
            wrapper.addEventListener('touchstart', () => {
                this.showTooltipMobile(node);
            }, { passive: true });

            // Click / tap to purchase
            wrapper.addEventListener('click', () => {
                if (canBuy) {
                    this.prestigeManager.purchaseElderTier(node.branchId as any);
                    this.updateUI();
                    // Refresh autoclicker if this tier affects it
                    this.scene?.refreshAutoClicker();
                    achievementManager.checkProgress('legacy_spent', gameStateManager.permanent.prestige.spentLegacy);
                } else if (!isUnlocked) {
                    el.style.animation = 'none';
                    void el.offsetWidth;
                    el.style.animation = '';
                    el.style.filter = 'drop-shadow(0 0 18px #ff4444bb)';
                    setTimeout(() => {
                        el.style.filter = isUnlocked
                            ? `drop-shadow(0 0 10px ${branchColor}bb)`
                            : canBuy ? `drop-shadow(0 0 6px ${branchColor}77)` : '';
                    }, 400);
                }
            });

            this.nodesContainer.appendChild(wrapper);
        });
    }

    // ---- Tooltip ----

    private hideTooltip() {
        this.tooltip.classList.add('hidden');
    }

    private showTooltip(node: PrestigeNodeDef, e: MouseEvent) {
        this.populateTooltip(node);
        this.tooltip.classList.remove('hidden');
        this.moveTooltip(e);
    }

    private showTooltipMobile(node: PrestigeNodeDef) {
        this.populateTooltip(node);
        this.tooltip.classList.remove('hidden');
        // Position is handled by CSS @media (hover: none)
    }

    private populateTooltip(node: PrestigeNodeDef) {
        this.ttTitle.textContent = node.name;
        this.ttDesc.textContent = node.description;
        this.ttCost.textContent = `${node.cost} Legacy`;

        const isUnlocked = this.isNodeUnlocked(node.id);
        const canBuy = this.canBuyNode(node.id);

        this.ttStatus.className = 'ptree-tt-status';
        if (isUnlocked) {
            this.ttStatus.textContent = 'Unlocked';
            this.ttStatus.classList.add('status-unlocked');
        } else if (canBuy) {
            this.ttStatus.textContent = 'Tap to Unlock';
            this.ttStatus.classList.add('status-buy');
        } else {
            this.ttStatus.textContent = 'Locked';
            this.ttStatus.classList.add('status-locked');
        }
    }

    private moveTooltip(e: MouseEvent) {
        let x = e.clientX + 16;
        let y = e.clientY + 16;

        const tipRect = this.tooltip.getBoundingClientRect();
        if (x + tipRect.width > window.innerWidth) x = e.clientX - tipRect.width - 16;
        if (y + tipRect.height > window.innerHeight) y = e.clientY - tipRect.height - 16;

        this.tooltip.style.left = `${x}px`;
        this.tooltip.style.top = `${y}px`;
    }
}
